import { z } from 'zod';
import {
    ReportChromeDocumentContextSchema,
    ReportPresentationSchema,
} from './report-chrome.js';
import {
    ReportBrandingLogoSchema,
    ReportBrandingSnapshotSchema,
} from './report-branding.schema.js';

export const REPORT_SESSION_SCHEMA_VERSION = 4;

const id = z.string().trim().min(1).max(200);
const stableKey = z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const selectorScalar = z.union([z.string().max(500), z.number().finite(), z.boolean()]);
const selectorValue = z.union([selectorScalar, z.array(selectorScalar).max(100)]);
const base64 = z.string().min(4).max(2_796_204).regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/);

export const ProgrammaticReportBrandingLogoSchema = ReportBrandingLogoSchema.extend({
    dataBase64: base64,
}).strict().superRefine((value, ctx) => {
    const padding = value.dataBase64.endsWith('==') ? 2 : value.dataBase64.endsWith('=') ? 1 : 0;
    const decodedSize = (value.dataBase64.length / 4) * 3 - padding;
    if (decodedSize !== value.size) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dataBase64'], message: 'Logo bytes must match the declared size' });
    }
});

export const ProgrammaticReportBrandingSnapshotSchema = ReportBrandingSnapshotSchema.omit({ logo: true }).extend({
    logo: ProgrammaticReportBrandingLogoSchema.nullable(),
}).strict();

const confidentiality = z.object({
    classId: z.string().trim().min(1).max(120),
    code: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(160),
    level: z.number().int().min(0).max(1000),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).transform((value) => value.toLowerCase()),
    watermark: z.object({
        enabled: z.boolean(),
        text: z.string().trim().max(160),
    }).strict().optional(),
}).strict();

const selectors = z.record(z.string().trim().min(1).max(120), selectorValue)
    .superRefine((value, ctx) => {
        if (Object.keys(value).length > 50) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'At most 50 selectors are allowed' });
        }
    });

const authorizedIds = z.array(id).max(25_000).superRefine((values, ctx) => {
    if (new Set(values).size !== values.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Authorized IDs must be unique' });
    }
    if (values.some((value, index) => index > 0 && values[index - 1].localeCompare(value) > 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Authorized IDs must be sorted' });
    }
});

export const ReportAuthorizationBindingSchema = z.object({
    mode: z.enum(['filtered', 'selected', 'single', 'tenant']),
    selectors,
    authorizedIds,
    selectorDigest: sha256,
    secondaryVisibilityDigest: sha256,
    authorizationRevision: z.string().trim().min(1).max(200),
    scopeDigest: sha256,
    bindingDigest: sha256,
}).strict();

export const ReportAuthorizationAttestationSchema = ReportAuthorizationBindingSchema.omit({
    bindingDigest: true,
}).strict();

export const ProgrammaticReportSessionSchema = z.object({
    schemaVersion: z.literal(REPORT_SESSION_SCHEMA_VERSION),
    definition: z.object({
        reportKey: stableKey,
        definitionVersion: z.number().int().positive(),
        definitionChecksum: sha256,
        rendererContractVersion: z.number().int().positive(),
        moduleKey: stableKey,
        moduleVersion: z.number().int().positive(),
        inputContractKey: stableKey,
        inputContractVersion: z.number().int().positive(),
        documentSpecVersion: z.number().int().positive(),
    }).strict(),
    subject: z.object({
        tenantId: id,
        userId: id,
        accessGrantId: id,
    }).strict(),
    locale: z.enum(['tr', 'en']),
    generatedAtUtc: z.coerce.date(),
    presentation: ReportPresentationSchema,
    documentContext: ReportChromeDocumentContextSchema,
    branding: z.object({
        version: z.number().int().positive(),
        revisionId: id,
        snapshot: ProgrammaticReportBrandingSnapshotSchema,
        digest: sha256,
    }).strict(),
    profile: z.object({
        id,
        profileVersion: z.number().int().positive(),
        contractKey: stableKey,
        contractVersion: z.number().int().positive(),
        normalizedOptions: z.record(z.string(), z.unknown()),
        confidentialitySnapshot: z.record(z.string(), z.unknown()),
        digest: sha256,
    }).strict().nullable(),
    confidentiality: confidentiality.nullable().optional(),
    authorization: ReportAuthorizationBindingSchema,
    input: z.record(z.string().trim().min(1).max(120), z.unknown()),
    inputDigest: sha256,
}).strict().superRefine((value, ctx) => {
    let encoded;
    try {
        encoded = JSON.stringify(value.input);
    } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['input'], message: 'Report input must be JSON serializable' });
        return;
    }
    if (new TextEncoder().encode(encoded).length > 10_485_760) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['input'], message: 'Report input exceeds the hard payload limit' });
    }
});

export const ReportViewerAuthorizationCallbackSchema = z.object({
    action: z.enum(['viewer-resolve', 'viewer-export', 'archive-create']),
    sessionId: sha256,
    reportKey: stableKey,
    definitionVersion: z.number().int().positive(),
    tenantId: id,
    userId: id,
    accessGrantId: id,
    bindingDigest: sha256,
}).strict();
